import { DisplayedAdUnit, isDeviceEnabled } from '@pubstack/common/src/adunit'
import { SiteStacksByProperty } from '@pubstack/common/src/stack'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, useState } from 'react'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { Icon } from '~/components/Icon'
import { Qualifier } from '~/components/Qualifier'
import { Tooltip } from '~/components/Tooltip'
import { StackUsage } from '../../StackUsage'
import { StackUsageDetails } from '../../StackUsageDetails'
import { useAdFormat } from './AdFormatDisplay'
import { AdUnitMediatypesDetails } from './components/AdUnitMediatypesDetails'

type PureAdmAdUnitDetailsFlyoutProps = {
  adUnit: DisplayedAdUnit
  sitesStacks?: SiteStacksByProperty
  onEditAdUnit: (adunitId: string) => void
}

const title = 'font-rubik text-base leading-6.5 font-medium'
const detailsWrapper = 'flex max-w-[816px] flex-col gap-3.5 py-2.5 font-roboto text-base leading-6.5'
const detailWrapper = 'flex gap-2'
const detailBloc = 'flex w-[243px] flex-col gap-0.5 [&>span]:overflow-hidden [&>span]:text-ellipsis'
const detailLabel = 'font-roboto text-sm leading-5.5 font-normal text-slate-grey'
const iconWrapper = 'flex gap-2 pt-1.5 pb-2.5'
const noValueText = 'text-ash italic'
/* The legacy Tooltip's trigger is `inline`, which leaves a baseline gap under the round icon it wraps. */
const qualifier = 'inline-flex'

const pipeSeparator = `\xa0 | \xa0`

export const PureAdmAdUnitDetailsFlyout: FunctionComponent<PureAdmAdUnitDetailsFlyoutProps> = ({ adUnit, sitesStacks, onEditAdUnit }) => {
  const adFormat = useAdFormat(adUnit.adFormat?.id)
  // Read here rather than through a prop: the flyout is opened from two places on the list, and the gate
  // belongs to the action, not to whichever row opened it.
  const { canEdit, disabledReason } = useScopePermissions()
  const [showUsage, setShowUsage] = useState(false)
  if (showUsage) return <StackUsageDetails name={adUnit.name} stacksUse={sitesStacks?.[adUnit.id]} onBack={() => setShowUsage(false)} />

  return (
    <Flyout.Content>
      <Flyout.Title>{adUnit.name}</Flyout.Title>
      <Flyout.Body>
        <div className={detailsWrapper}>
          <h2 className={title}>Identification</h2>
          <div className={detailWrapper}>
            <div>
              <div className={detailBloc}>
                <h3 className={detailLabel}>Name</h3>
                {/* Renaming is unwired for now — the pen that opened `RenameAdUnitModal` is gone from here and
                    from the Node flyout. The modal and its spec are left in the tree: the name leaves the
                    platform, reaching bidders and keying mapping files, so this is a pause on offering the
                    act rather than a decision to drop it. */}
                <span>{adUnit.name}</span>
              </div>
            </div>
            <div>
              <div className={detailBloc}>
                <h3 className={detailLabel}>Ad unit name in the ad server</h3>
                <span>{adUnit.adServerAdUnitName}</span>
              </div>
            </div>
            <div>
              <div className={detailBloc}>
                <h3 className={detailLabel}>Technical identification (div ID)</h3>
                <span>{adUnit.divId}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={detailsWrapper}>
          <div className={detailWrapper}>
            <div className={detailBloc}>
              <h2 className={title}>Devices</h2>
              <div className={iconWrapper}>
                <Qualifier
                  className={qualifier}
                  iconName={'desktop'}
                  tooltipText={isDeviceEnabled('desktop', adUnit.devices) ? 'Desktop enabled' : 'Desktop disabled'}
                  color={Colors.Petrol}
                  active={isDeviceEnabled('desktop', adUnit.devices)}
                  enabled={adUnit.enabled}
                />
                <Qualifier
                  className={qualifier}
                  iconName={'mobile'}
                  tooltipText={isDeviceEnabled('mobile', adUnit.devices) ? 'Mobile enabled' : 'Mobile disabled'}
                  color={Colors.Pool}
                  active={isDeviceEnabled('mobile', adUnit.devices)}
                  enabled={adUnit.enabled}
                />
              </div>
            </div>
            <div className={detailBloc}>
              <h2 className={title}>Format</h2>
              <div className={iconWrapper}>
                <div className={'inline-flex items-center gap-1'}>
                  <Icon className={'[--format-icon-color:var(--color-turquoise)]'} name={adFormat?.iconName ?? 'custom'} width={'34px'} height={'34px'} />
                  <span>{adFormat?.name}</span>
                </div>
              </div>
            </div>
            {!adUnit.isInstream && (
              <div className={detailBloc}>
                <h2 className={title}>Behaviour</h2>
                <div className={iconWrapper}>
                  <Qualifier
                    className={qualifier}
                    iconName={'dynamic'}
                    tooltipText={adUnit.dynamicEnabled ? 'Dynamic enabled' : 'Dynamic disabled'}
                    color={Colors.Success}
                    active={adUnit.dynamicEnabled}
                    enabled={adUnit.enabled}
                  />
                  <Qualifier
                    className={qualifier}
                    iconName={'parallax'}
                    tooltipText={adUnit.parallaxEnabled ? 'Parallax enabled' : 'Parallax disabled'}
                    color={Colors.Success}
                    active={adUnit.parallaxEnabled}
                    enabled={adUnit.enabled}
                  />
                  <Qualifier
                    className={qualifier}
                    iconName={'anchor'}
                    tooltipText={adUnit.anchor ? 'Anchor enabled' : 'Anchor disabled'}
                    color={Colors.Success}
                    active={!!adUnit.anchor}
                    enabled={adUnit.enabled}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {!adUnit.isInstream && (
          <div className={detailsWrapper}>
            <h2 className={title}>Max size filter</h2>
            <div className={detailWrapper}>
              <div>
                <div className={detailBloc}>
                  <h3 className={detailLabel}>Max width</h3>
                  <span>{adUnit.containerMaxSize?.width ? `${adUnit.containerMaxSize.width} px` : <div className={noValueText}>Not defined</div>}</span>
                </div>
              </div>
              <div>
                <div className={detailBloc}>
                  <h3 className={detailLabel}>Max height</h3>
                  <span>{adUnit.containerMaxSize?.height ? `${adUnit.containerMaxSize.height} px` : <div className={noValueText}>Not defined</div>}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className={detailsWrapper}>
          <div className={detailWrapper}>
            <div className={detailBloc}>
              <h2 className={title}>Demand source</h2>
              <div className={iconWrapper}>
                <Qualifier
                  className={qualifier}
                  iconName={'header_bidding'}
                  tooltipText={adUnit.headerBiddingEnabled ? 'Header bidding enabled' : 'Header bidding disabled'}
                  color={Colors.Success}
                  active={adUnit.headerBiddingEnabled}
                  enabled={adUnit.enabled}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={'pt-2.5 pb-4'}>
          <h2 className={title}>Media types</h2>
          <div className={detailsWrapper}>
            <div className={detailWrapper}>
              <AdUnitMediatypesDetails adUnit={adUnit} />
            </div>
          </div>
        </div>

        <h2 className={title}>Key-values</h2>

        {adUnit.slotKeyValues && adUnit.slotKeyValues?.length > 0 ? (
          adUnit.slotKeyValues?.map((element) => (
            <div key={element.key} className={detailsWrapper}>
              <div className={detailBloc}>
                <h3 className={detailLabel}>Key</h3>
                <span>{element.key}</span>
              </div>
              <div className={cn(detailBloc, 'w-full')}>
                <h3 className={detailLabel}>Values</h3>
                <span>{element.values.join(pipeSeparator)}</span>
              </div>
            </div>
          ))
        ) : (
          <div className={cn(noValueText, 'py-2')}>No key-values used.</div>
        )}

        {/* Spaced here rather than by the section: this body stacks its blocks without a gap, the other flyouts with one. */}
        <StackUsage className={'py-2'} stacksUse={sitesStacks?.[adUnit.id]} onSeeAll={() => setShowUsage(true)} />
      </Flyout.Body>
      <Flyout.Actions>
        {/* Archived has never been editable here, and the tooltip has nothing to say about that — only about the gate. */}
        <Tooltip title={disabledReason} disabled={canEdit} reposition>
          <Button
            variant={'secondary'}
            onClick={() => {
              onEditAdUnit(adUnit.id)
            }}
            iconName={'edit'}
            disabled={!adUnit.enabled || !canEdit}
            trackingLabel={'adm-ad-units-detail-edit'}
          >
            Edit
          </Button>
        </Tooltip>
      </Flyout.Actions>
    </Flyout.Content>
  )
}
